import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { submitContactForm } from '@/lib/api';
const subjectOptions = [
    'Event Enquiry',
    'Ticket Issue',
    'Partnership / Sponsorship',
    'Media & Press',
    'Artist / Performer',
    'General Enquiry',
];
export default function ContactForm() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const sectionRef = useRef(null);
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.querySelectorAll('.reveal-up').forEach((el) => {
                        el.classList.add('revealed');
                    });
                    entry.target.querySelectorAll('.reveal-fade').forEach((el) => {
                        el.classList.add('revealed');
                    });
                }
            });
        }, { threshold: 0.1 });
        if (sectionRef.current)
            observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);
    const handleChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitContactForm({
                fullName: formData.name.trim(),
                email: formData.email.trim(),
                phoneNumber: formData.phone.trim() || undefined,
                subject: formData.subject,
                message: formData.message.trim(),
            });
            setSubmitted(true);
        }
        catch {
            setLoading(false);
            alert('Unable to send your message. Please try again.');
            return;
        }
        setLoading(false);
    };
    return (<div ref={sectionRef} className="bg-secondary border-t border-border/40 py-14 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left: Info */}
          <div className="reveal-up flex flex-col gap-7">
            <div>
              <p className="section-label mb-3">Send a Message</p>
              <h2 className="font-display text-section-title font-semibold text-foreground gold-underline">
                How Can We Help?
              </h2>
            </div>
            <p className="text-muted-foreground leading-relaxed text-base mt-3">
              Whether you have questions about upcoming events, need help with tickets, or want to explore collaboration opportunities — our team is ready to assist.
            </p>

            {/* Social Links */}
            <div>
              <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-4">Follow Us</p>
              <div className="flex items-center gap-4">
                {[
            { label: 'Facebook', icon: 'GlobeAltIcon', handle: '@awakashaentertainment' },
            { label: 'Instagram', icon: 'CameraIcon', handle: '@awakasha.ent' },
            { label: 'YouTube', icon: 'PlayCircleIcon', handle: 'Awakasha Entertainment' },
            { label: 'TikTok', icon: 'MusicalNoteIcon', handle: '@awakasha' },
        ].map((s) => (<button key={s.label} aria-label={s.label} className="social-icon-hover w-10 h-10 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground">
                    <Icon name={s.icon} size={18} variant="outline"/>
                  </button>))}
              </div>
            </div>

            {/* Map placeholder */}
            <div id="map" className="reveal-fade relative h-48 rounded-xl overflow-hidden border border-border/50 bg-muted flex items-center justify-center">
              <div className="text-center">
                <Icon name="MapIcon" size={32} className="text-primary mx-auto mb-2"/>
                <p className="text-muted-foreground text-sm">Melbourne, Victoria, Australia</p>
                <p className="text-xs text-muted-foreground mt-1">Serving all of Australia</p>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="reveal-up" style={{ transitionDelay: '150ms' }}>
            {submitted ? (<div className="h-full flex flex-col items-center justify-center text-center py-16 gap-5">
                <div className="w-16 h-16 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center">
                  <Icon name="CheckIcon" size={28} className="text-primary"/>
                </div>
                <div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-2">Message Sent!</h3>
                  <p className="text-muted-foreground text-sm max-w-xs mx-auto">
                    Thank you for reaching out. We&apos;ll get back to you within 24 hours.
                  </p>
                </div>
                <button onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); }} className="btn-outline-gold px-6 py-2.5 rounded-full text-sm mt-2 inline-flex items-center gap-2">
                  Send Another Message
                </button>
              </div>) : (<form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {/* Name + Email Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="name" className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                      Full Name *
                    </label>
                    <input id="name" name="name" type="text" value={formData.name} onChange={handleChange} placeholder="Chamara Perera" required className="form-input-gold"/>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                      Email Address *
                    </label>
                    <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="chamara@email.com" required className="form-input-gold"/>
                  </div>
                </div>

                {/* Phone + Subject Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="phone" className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                      Phone Number
                    </label>
                    <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="+61 4XX XXX XXX" className="form-input-gold"/>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="subject" className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                      Subject *
                    </label>
                    <select id="subject" name="subject" value={formData.subject} onChange={handleChange} required className="form-input-gold" style={{ background: 'var(--muted)', color: formData.subject ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
                      <option value="" disabled>Select a subject</option>
                      {subjectOptions.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="message" className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                    Message *
                  </label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your enquiry..." required rows={5} className="form-input-gold resize-none"/>
                </div>

                {/* Submit */}
                <button type="submit" disabled={loading} className="btn-gold flex items-center justify-center gap-2.5 w-full py-4 rounded-xl text-sm font-bold tracking-widest uppercase mt-2 disabled:opacity-70">
                  {loading ? (<>
                      <Icon name="ArrowPathIcon" size={16} className="animate-spin text-black"/>
                      <span>Sending...</span>
                    </>) : (<>
                      <span>Send Message</span>
                      <Icon name="PaperAirplaneIcon" size={16} className="text-black"/>
                    </>)}
                </button>

                <p className="text-xs text-muted-foreground text-center">
                  We respect your privacy. Your information will never be shared.
                </p>
              </form>)}
          </div>
        </div>
      </div>
    </div>);
}
